/**
 * LayoutThumbnail.tsx — a small SVG sketch of a template's layout family.
 *
 * Port of the mockup's `_layThumb`, keyed on the catalog's own `layoutClass`
 * vocabulary (`shared/templatesignature/vector.py::LAYOUT_CLASSES`) so every
 * class the ranker can return has a shape.
 *
 * Deliberately a sketch, not a preview. A true preview would mean rendering the
 * template's slide, which needs the `.pptx` — and the picker lists up to a dozen
 * candidates the user may never choose, so fetching and rendering all of them to
 * draw a grid would be far more work than the choice warrants. The sketch shows
 * the one thing that drives the choice: how the content is arranged.
 */

import { useId } from "react";

/** Block fills — the mockup's three blues. */
const STRONG = "#7fa8ce";
const BLOCK = "#b4d0ea";
const FAINT = "#e4edf6";

interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly fill?: string;
  readonly r?: number;
}

/** N evenly spaced columns across the body area. */
function columns(count: number): Rect[] {
  const gap = 3;
  const left = 4;
  const usable = 56 - gap * (count - 1);
  const width = usable / count;
  return Array.from({ length: count }, (_, index) => ({
    x: left + index * (width + gap),
    y: 12,
    w: width,
    h: 18,
    fill: index === 0 ? BLOCK : FAINT,
    r: 1.5,
  }));
}

const TITLE_BAR: Rect = { x: 4, y: 4, w: 32, h: 3, fill: STRONG };

/** Rect sets per layout class. `lines` draws a timeline rail instead. */
const SHAPES: Record<string, { readonly rects: Rect[]; readonly rail?: boolean }> = {
  cover: {
    rects: [
      { x: 8, y: 11, w: 48, h: 7, fill: STRONG, r: 1.5 },
      { x: 14, y: 21, w: 36, h: 3, fill: BLOCK },
    ],
  },
  divider: {
    rects: [
      { x: 0, y: 13, w: 64, h: 10, fill: STRONG },
      { x: 8, y: 26, w: 26, h: 3, fill: FAINT },
    ],
  },
  closing: {
    rects: [
      { x: 16, y: 12, w: 32, h: 6, fill: STRONG, r: 1.5 },
      { x: 22, y: 21, w: 20, h: 3, fill: BLOCK },
    ],
  },
  agenda: {
    rects: [
      TITLE_BAR,
      ...[0, 1, 2, 3].map((i) => ({ x: 6, y: 11 + i * 5, w: 44, h: 3, fill: i ? FAINT : BLOCK })),
    ],
  },
  list: {
    rects: [
      TITLE_BAR,
      ...[0, 1, 2, 3].map((i) => ({ x: 4 + i * 15, y: 12, w: 13, h: 16, fill: i ? FAINT : BLOCK, r: 1.5 })),
    ],
  },
  statistical: {
    rects: [
      { x: 4, y: 3, w: 30, h: 3, fill: STRONG },
      ...[0, 1, 2, 3].map((i) => ({
        x: 6 + (i % 2) * 28,
        y: 9 + Math.floor(i / 2) * 12,
        w: 24,
        h: 9,
        fill: i ? FAINT : BLOCK,
        r: 1.5,
      })),
    ],
  },
  swot: {
    rects: [
      ...[0, 1, 2, 3].map((i) => ({
        x: 5 + (i % 2) * 28,
        y: 5 + Math.floor(i / 2) * 14,
        w: 26,
        h: 12,
        fill: i % 3 === 0 ? BLOCK : FAINT,
        r: 1.5,
      })),
    ],
  },
  process: {
    rects: [
      TITLE_BAR,
      ...[0, 1, 2].map((i) => ({
        x: 6 + i * 18,
        y: 14,
        w: 15,
        h: 10,
        fill: i === 2 ? STRONG : BLOCK,
        r: 1.5,
      })),
    ],
  },
  hierarchical: {
    rects: [
      { x: 26, y: 5, w: 12, h: 7, fill: STRONG, r: 1.5 },
      { x: 8, y: 20, w: 12, h: 7, fill: BLOCK, r: 1.5 },
      { x: 26, y: 20, w: 12, h: 7, fill: BLOCK, r: 1.5 },
      { x: 44, y: 20, w: 12, h: 7, fill: BLOCK, r: 1.5 },
    ],
  },
  timeline: { rects: [TITLE_BAR], rail: true },
  "1-column": {
    rects: [
      TITLE_BAR,
      { x: 4, y: 10, w: 56, h: 3, fill: FAINT },
      { x: 4, y: 15, w: 50, h: 3, fill: FAINT },
      { x: 4, y: 20, w: 54, h: 3, fill: FAINT },
      { x: 4, y: 25, w: 40, h: 3, fill: FAINT },
    ],
  },
  "2-column": { rects: [TITLE_BAR, ...columns(2)] },
  "3-column": { rects: [TITLE_BAR, ...columns(3)] },
  "4-column": { rects: [TITLE_BAR, ...columns(4)] },
  "5-column": { rects: [TITLE_BAR, ...columns(5)] },
  "6plus-column": { rects: [TITLE_BAR, ...columns(6)] },
};

const FALLBACK = SHAPES["1-column"]!;

export interface LayoutThumbnailProps {
  readonly layoutClass?: string | null;
  readonly className?: string;
  /** Names the sketch for assistive tech, e.g. the template's own name. */
  readonly label?: string;
}

export function LayoutThumbnail({ layoutClass, className, label }: Readonly<LayoutThumbnailProps>) {
  const titleId = useId();
  const shape = (layoutClass && SHAPES[layoutClass]) || FALLBACK;
  const title = label ?? `${layoutClass ?? "content"} layout`;

  return (
    <svg
      viewBox="0 0 64 36"
      className={className}
      role="img"
      aria-labelledby={titleId}
      preserveAspectRatio="none"
    >
      <title id={titleId}>{title}</title>
      <rect width="64" height="36" fill="#ffffff" />
      {shape.rects.map((rect) => (
        <rect
          key={`${rect.x}-${rect.y}-${rect.w}-${rect.h}`}
          x={rect.x}
          y={rect.y}
          width={rect.w}
          height={rect.h}
          rx={rect.r ?? 1}
          fill={rect.fill ?? BLOCK}
        />
      ))}
      {shape.rail ? (
        <>
          <line x1="6" y1="20" x2="58" y2="20" stroke={STRONG} strokeWidth="1.5" />
          {[0, 1, 2, 3].map((i) => (
            <circle key={i} cx={11 + i * 14} cy={20} r={2.5} fill={i % 2 ? BLOCK : STRONG} />
          ))}
        </>
      ) : null}
    </svg>
  );
}
